import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  useColorModeValue,
} from "@chakra-ui/react";
import Header from "../Common/Header";
import Footer from "../Common/Footer";
import { MdCheck } from "react-icons/md";

interface ComplianceSection {
  id: string;
  title: string;
  content: React.ReactNode;
  subsections?: ComplianceSection[];
}

const ComplianceFramework: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const importantBg = useColorModeValue("red.50", "red.900");
  const footerBg = useColorModeValue("gray.50", "gray.700");

  const complianceData: ComplianceSection[] = [
    {
      id: "1",
      title: "Introduction and Compliance Philosophy",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            This Compliance Framework Document ("Framework") establishes the
            regulatory compliance standards, professional obligations, and
            operational requirements for healthcare professionals using QIQ
            Health by VFrequentists LLP ("QIQ Health," "Platform," "Service").
          </Text>
          <Text>
            This Framework is designed to support healthcare professionals in
            maintaining compliance with applicable laws, regulations, and
            professional standards while using QIQ Health as a clinical
            decision-support tool.
          </Text>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Compliance Scope
            </Heading>
            <Text mb={3}>
              This Framework addresses compliance requirements for:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare professionals using QIQ Health in clinical practice
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Healthcare institutions deploying QIQ Health for operational use
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Medical coding and billing professionals utilizing QIQ Health
                for revenue cycle management
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                VFrequentists LLP's operational compliance as a healthcare
                technology provider
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Compliance Responsibility
            </Heading>
            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
            >
              <Text fontWeight="bold" color="red.700">
                FUNDAMENTAL PRINCIPLE: Healthcare professionals remain solely
                responsible for compliance with all applicable regulatory
                requirements, professional standards, and institutional policies
                in their use of QIQ Health. VFrequentists LLP provides a
                compliant technology platform but does not assume responsibility
                for professional compliance decisions made by healthcare users.
              </Text>
            </Box>
          </Box>
        </VStack>
      ),
    },
    {
      id: "2",
      title: "Regulatory Compliance Framework",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              HIPAA Privacy and Security Rule Compliance
            </Heading>
            <Text mb={3}>
              VFrequentists LLP maintains HIPAA compliance through:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Technical Safeguards:
                </Text>{" "}
                AES-256 encryption, secure access controls, audit logging
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Administrative Safeguards:
                </Text>{" "}
                Security officer designation, workforce training, assigned
                security responsibilities
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Physical Safeguards:
                </Text>{" "}
                Secure data center facilities, workstation use controls, device
                and media controls
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Business Associate Agreements:
                </Text>{" "}
                Executed BAAs with all third-party service providers handling
                PHI
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional HIPAA Obligations
            </Heading>
            <Text mb={3}>Healthcare professionals using QIQ Health must:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Ensure all PHI entered is within their scope of access and
                need-to-know
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain minimum necessary standards for PHI access and
                disclosure
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Implement appropriate workforce training and access controls
                within their practice
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Report suspected HIPAA violations or security incidents to
                privacy@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain HIPAA compliance documentation and policies within
                their practice environment
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              FDA Regulatory Compliance
            </Heading>
            <Text mb={3}>QIQ Health is classified as:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Clinical Decision Support Software:
                </Text>{" "}
                Provides information to healthcare professionals to support
                clinical decision-making
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Non-Device Software:
                </Text>{" "}
                Does not meet FDA criteria for medical device regulation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Use Tool:
                </Text>{" "}
                Designed exclusively for qualified healthcare professionals
                within their scope of practice
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "3",
      title: "Professional Standards Compliance",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Medical Coding and Billing Compliance
            </Heading>
            <Text mb={3}>
              Healthcare professionals using QIQ Health for medical coding must
              ensure:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  ICD-10-CM/PCS Compliance:
                </Text>{" "}
                All diagnostic and procedure codes align with current ICD-10
                standards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  CPT Coding Accuracy:
                </Text>{" "}
                Current Procedural Terminology codes reflect actual services
                provided
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  HCPCS Compliance:
                </Text>{" "}
                Healthcare Common Procedure Coding System codes used
                appropriately
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Documentation Requirements:
                </Text>{" "}
                Adequate clinical documentation supports all coded services
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Clinical Practice Standards
            </Heading>
            <Text mb={3}>
              Healthcare professionals must ensure QIQ Health use:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Supports, not replaces, appropriate clinical judgment
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Aligns with evidence-based clinical practice guidelines
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintains patient safety as the primary consideration
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Complies with specialty-specific professional standards
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "4",
      title: "Technical Compliance Requirements",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Data Security and Privacy Compliance
            </Heading>
            <Text mb={3}>Healthcare professionals must:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Access Control:
                </Text>{" "}
                Use unique user credentials and maintain password security
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Multi-Factor Authentication:
                </Text>{" "}
                Implement where available
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Session Management:
                </Text>{" "}
                Ensure appropriate session management and timeout procedures
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Incident Reporting:
                </Text>{" "}
                Report unauthorized access immediately to security@weqiq.com
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              System Integration Compliance
            </Heading>
            <Text mb={3}>
              When integrating QIQ Health with Electronic Health Records:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Ensure data mapping accuracy and completeness
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain audit trails for all data transfers
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Comply with healthcare data interoperability standards (HL7,
                FHIR)
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Validate integration security and privacy protections
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "5",
      title: "Monitoring and Audit Framework",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Compliance Monitoring Requirements
            </Heading>
            <Text mb={3}>Healthcare professionals should:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Regularly review their QIQ Health usage for compliance with
                professional standards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Conduct periodic assessments of data accuracy and clinical
                appropriateness
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Monitor for any security incidents or unauthorized access
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain documentation of compliance activities and assessments
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Audit Trail Maintenance
            </Heading>
            <Text mb={3}>
              QIQ Health maintains comprehensive audit trails including:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                User access logs and authentication records
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Data modification and access history
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                System configuration changes and updates
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Security incident logs and response documentation
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "6",
      title: "Incident Response and Compliance Violations",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Security Incident Response
            </Heading>
            <Text mb={3}>
              Healthcare professionals must immediately report to VFrequentists
              LLP:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Suspected unauthorized access to PHI or clinical data
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Potential data breaches or security vulnerabilities
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                System malfunctions affecting clinical operations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Any concerns about data integrity or accuracy
              </ListItem>
            </List>
            <Box
              bg={importantBg}
              border="2px solid"
              borderColor="red.300"
              borderRadius="md"
              p={4}
              mt={3}
            >
              <Text fontWeight="bold" color="red.700">
                Emergency Contact: security@weqiq.com
              </Text>
              <Text fontWeight="bold" color="red.700">
                Response Time: 24-hour acknowledgment, investigation within 48
                hours
              </Text>
            </Box>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Breach Response Procedures
            </Heading>
            <Text mb={3}>
              VFrequentists LLP maintains breach response procedures including:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Immediate incident containment and investigation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Risk assessment and impact analysis
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Notification to affected healthcare professionals within 72
                hours
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Regulatory reporting as required by HIPAA and applicable laws
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Remediation and corrective action implementation
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "7",
      title: "Training and Competency Requirements",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Initial Training Requirements
            </Heading>
            <Text mb={3}>All healthcare professionals must complete:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                HIPAA privacy and security training specific to QIQ Health
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Platform functionality and appropriate use training
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional compliance requirements education
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Clinical decision-making best practices with technology support
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Ongoing Compliance Education
            </Heading>
            <Text mb={3}>Healthcare professionals should participate in:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Annual compliance refresher training
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Updates on regulatory changes affecting QIQ Health use
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Professional development in health information technology
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Specialty-specific compliance education where applicable
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "8",
      title: "Third-Party Compliance and Vendor Management",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Business Associate Management
            </Heading>
            <Text mb={3}>
              All VFrequentists LLP vendors and business associates must:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain current HIPAA compliance certifications
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Provide evidence of security and privacy safeguards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Submit to regular compliance audits and assessments
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintain appropriate professional liability and cyber insurance
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Integration Partner Compliance
            </Heading>
            <Text mb={3}>Integration partners must demonstrate:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                HIPAA compliance and appropriate safeguards
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Data interoperability standards compliance (HL7, FHIR)
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Security and encryption capabilities
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Audit trail and logging functionality
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "9",
      title: "Regulatory Reporting and Communication",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Regulatory Agency Communication
            </Heading>
            <Text mb={3}>
              VFrequentists LLP maintains appropriate communication with FDA
              including:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Software classification and regulatory status documentation
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Adverse event reporting procedures where applicable
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Post-market surveillance and safety monitoring
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Regulatory change monitoring and compliance updates
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional and Institutional Reporting
            </Heading>
            <Text mb={3}>
              Healthcare professionals may be required to report:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Technology-related adverse events or patient safety concerns
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Compliance violations or professional misconduct
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Changes in professional status or licensing
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Institutional policy violations involving QIQ Health
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "10",
      title: "Compliance Assessment and Continuous Improvement",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Regular Compliance Assessment
            </Heading>
            <Text mb={3}>
              VFrequentists LLP provides healthcare professionals with:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Compliance checklists and assessment tools
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Best practice guidelines and recommendations
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Self-audit procedures and documentation templates
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Compliance scorecards and performance metrics
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Professional Compliance Reviews
            </Heading>
            <Text mb={3}>Healthcare professionals should conduct:</Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Quarterly compliance self-assessments
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Annual comprehensive compliance reviews
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Peer review and consultation where appropriate
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Integration with professional development planning
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "11",
      title: "Contact Information and Support",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Compliance Support Contacts
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Primary Compliance Officer:
                </Text>{" "}
                compliance@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Privacy and Security Officer:
                </Text>{" "}
                privacy@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Emergency Security:
                </Text>{" "}
                security@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Professional Support Team:
                </Text>{" "}
                support@weqiq.com
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Training and Education:
                </Text>{" "}
                training@weqiq.com
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Response Times
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Non-urgent matters:
                </Text>{" "}
                48 hours
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Urgent compliance issues:
                </Text>{" "}
                24 hours
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Security incidents:
                </Text>{" "}
                Immediate response
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "12",
      title: "Acknowledgment and Certification",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Healthcare Professional Acknowledgment
            </Heading>
            <Text mb={3}>
              By using QIQ Health, healthcare professionals acknowledge:
            </Text>
            <List spacing={2} styleType="decimal">
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Understanding of all compliance requirements outlined in this
                Framework
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Commitment to maintaining professional compliance standards
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Responsibility for independent compliance decision-making
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Agreement to report compliance concerns and incidents promptly
              </ListItem>
              <ListItem>
                <ListIcon as="span" color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Understanding that VFrequentists LLP provides a compliant
                platform but does not assume professional compliance
                responsibility
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Institutional Acknowledgment (Where Applicable)
            </Heading>
            <Text mb={3}>
              Healthcare institutions deploying QIQ Health acknowledge:
            </Text>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Integration of QIQ Health compliance requirements with
                institutional policies
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Oversight responsibility for professional user compliance
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Maintenance of institutional compliance programs and procedures
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                Coordination with VFrequentists LLP for compliance support and
                incident response
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Header minimal={false} />
      <Container maxW="container.xl" mt={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box
            textAlign="center"
            pb={8}
            borderBottom="3px solid"
            borderColor="blue.500"
          >
            <Heading as="h1" size="2xl" color="blue.600" mb={2}>
              Compliance Framework Document
            </Heading>
            <Heading as="h2" size="lg" color="gray.600" mb={6}>
              QIQ Health by VFrequentists LLP
            </Heading>

            <Box
              bg={headerBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              p={6}
            >
              <VStack spacing={2} align="stretch">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Effective Date:
                  </Text>{" "}
                  January 1, 2025
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Last Updated:
                  </Text>{" "}
                  August 11, 2025
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Compliance Content */}
          <Accordion allowMultiple defaultIndex={[0]}>
            {complianceData.map((section) => (
              <AccordionItem
                key={section.id}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                mb={4}
              >
                <AccordionButton
                  py={6}
                  px={6}
                  bg={headerBg}
                  _hover={{ bg: "blue.100" }}
                  _expanded={{
                    bg: "blue.200",
                    borderBottom: "2px solid",
                    borderColor: "blue.400",
                  }}
                >
                  <HStack flex="1" textAlign="left" spacing={4}>
                    <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                      {section.id}
                    </Badge>
                    <Text fontWeight="semibold" fontSize="lg">
                      {section.title}
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={8} px={8}>
                  {section.content}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Footer */}
          <Box
            bg={footerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
          >
            <VStack spacing={6} align="stretch">
              <Text fontStyle="italic" color="gray.600">
                This document has been prepared in accordance with HIPAA Privacy
                and Security Rules, FDA regulations, state healthcare laws, and
                professional standards. This Compliance Framework constitutes a
                legally binding framework for compliance obligations between you
                and VFrequentists LLP.
              </Text>

              <Divider />

              <VStack spacing={2} align="stretch">
                <Text fontWeight="semibold">VFrequentists LLP</Text>
                <Text fontWeight="semibold">Team QIQ Health</Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Document Version:
                  </Text>{" "}
                  1.0
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Status:
                  </Text>{" "}
                  Final Version - Effective as of January 1, 2025
                </Text>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default ComplianceFramework;
